import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../config/supabase';

const AuthContext = createContext(undefined);

function formatProfile(profile) {
  if (!profile) return null;
  return {
    id: profile.id,
    email: profile.email,
    firstName: profile.first_name,
    lastName: profile.last_name,
    displayName: profile.display_name || profile.first_name,
    avatar: profile.avatar,
    accountType: profile.account_type,
    partner: profile.partner_id,
    partnerName: profile.partner_name,
    coupleStatus: profile.couple_status,
    preferences: profile.preferences,
    stats: profile.stats,
    settings: profile.settings,
    isPremium: profile.is_premium,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    let initialCheckDone = false;

    async function checkAuth() {
      try {
        const sessionResult = await Promise.race([
          supabase.auth.getSession(),
          new Promise((resolve) => setTimeout(() => resolve(null), 8000)),
        ]);

        if (sessionResult && sessionResult.data?.session) {
          const session = sessionResult.data.session;
          setToken(session.access_token);
          setHasSession(true);
          await Promise.race([
            fetchProfile(session.user.id),
            new Promise((resolve) => setTimeout(resolve, 5000)),
          ]);
        }
      } catch (error) {
        console.error('Check auth error:', error);
        setToken(null);
        setUser(null);
      } finally {
        initialCheckDone = true;
        setIsLoading(false);
      }
    }

    checkAuth();

    const safetyTimer = setTimeout(() => {
      if (!initialCheckDone) {
        console.warn('Auth check timed out — forcing load complete');
        setIsLoading(false);
      }
    }, 10000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session) {
        setToken(session.access_token);
        setHasSession(true);
        await fetchProfile(session.user.id);
      } else {
        setToken(null);
        setUser(null);
        setHasSession(false);
      }
      if (!initialCheckDone) {
        initialCheckDone = true;
        setIsLoading(false);
      }
    });

    return () => {
      clearTimeout(safetyTimer);
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = useCallback(async (userId) => {
    try {
      const result = await Promise.race([
        supabase.from('profiles').select('*').eq('id', userId).single(),
        new Promise((resolve) =>
          setTimeout(() => resolve({ data: null, error: { message: 'Profile fetch timed out' } }), 10000)
        ),
      ]);

      const { data: profile, error } = result;

      if (error) {
        console.error('Fetch profile error:', error.message);
        setUser(null);
        return;
      }
      setUser(formatProfile(profile));
    } catch (err) {
      console.error('Fetch profile exception:', err);
      setUser(null);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const signInResult = await Promise.race([
      supabase.auth.signInWithPassword({ email: email.toLowerCase(), password }),
      new Promise((resolve) => setTimeout(() => resolve(null), 15000)),
    ]);

    if (!signInResult) throw new Error('Login timed out. Please check your connection and try again.');

    const { data, error } = signInResult;
    if (error) throw new Error(error.message);

    setToken(data.session.access_token);
    await fetchProfile(data.user.id);
  }, [fetchProfile]);

  const register = useCallback(async (registerData) => {
    const { email, password, firstName, lastName, referralCode } = registerData;

    const signUpResult = await Promise.race([
      supabase.auth.signUp({
        email: email.toLowerCase(),
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName || '',
            display_name: `${firstName}${lastName ? ' ' + lastName : ''}`,
          },
        },
      }),
      new Promise((resolve) => setTimeout(() => resolve(null), 15000)),
    ]);

    if (!signUpResult) throw new Error('Registration timed out. Please check your connection and try again.');

    const { data, error } = signUpResult;
    if (error) throw new Error(error.message);
    if (!data.session) throw new Error('Registration succeeded but no session returned. Please try logging in.');

    setToken(data.session.access_token);

    await supabase.from('profiles').update({
      first_name: firstName,
      last_name: lastName || null,
      display_name: `${firstName}${lastName ? ' ' + lastName : ''}`,
    }).eq('id', data.user.id);

    // Process referral code
    if (referralCode && referralCode.startsWith('HNG-')) {
      try {
        const referrerId = referralCode.replace('HNG-', '').toLowerCase();
        const { data: referrers } = await supabase
          .from('profiles')
          .select('id, stats')
          .ilike('id', `${referrerId}%`)
          .limit(1);

        if (referrers && referrers.length > 0) {
          const referrer = referrers[0];
          const referrerStats = referrer.stats || {};
          referrerStats.referralCount = (referrerStats.referralCount || 0) + 1;
          referrerStats.referralWeeksEarned = (referrerStats.referralWeeksEarned || 0) + 1;
          await supabase.from('profiles').update({ stats: referrerStats }).eq('id', referrer.id);
          await supabase.from('profiles').update({ stats: { referredBy: referrer.id } }).eq('id', data.user.id);
        }
      } catch (refErr) {
        console.error('Referral processing error:', refErr);
      }
    }

    await fetchProfile(data.user.id);
  }, [fetchProfile]);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setToken(null);
    setUser(null);
    setHasSession(false);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await fetchProfile(session.user.id);
      }
    } catch {
      // ignore
    }
  }, [fetchProfile]);

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
  }, []);

  const value = useMemo(
    () => ({ user, token, hasSession, isLoading, login, register, logout, updateUser, refreshUser }),
    [user, token, hasSession, isLoading, login, register, logout, updateUser, refreshUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
